/* Shared viewport owner for the native phone personalization dialogs. */
(function () {
  'use strict';
  function create({ dialog, isOpen, title, doneButton }) {
    const view = window.visualViewport;
    const previewButton = dialog.querySelector('[data-resize-preview]');
    const textSelector = 'input[type="text"], input:not([type]), textarea, [contenteditable="true"]';
    let baseline = 0, width = 0, keyboard = false, revealFrame = 0, lastGeometry = '', lastFocus = null;
    function previewSize(expanded) {
      if (!previewButton) return;
      dialog.dataset.previewSize = expanded ? 'expanded' : 'compact';
      previewButton.hidden = false;
      previewButton.setAttribute('aria-expanded', String(expanded));
      previewButton.textContent = expanded ? previewButton.dataset.reduce : previewButton.dataset.enlarge;
    }
    previewButton?.addEventListener('click', () => {
      if (!isOpen() || previewButton.disabled) return;
      // Only this explicit action changes preview size; scrolling never does.
      previewSize(dialog.dataset.previewSize !== 'expanded');
    });
    const focusedText = () => {
      const input = document.activeElement;
      return dialog.contains(input) && input?.matches(textSelector) ? input : null;
    };
    function clearSize() {
      dialog.style.removeProperty('--mobile-editor-height');
      dialog.style.removeProperty('--mobile-editor-top');
      delete dialog.dataset.keyboard;
    }
    function revealFocusedField() {
      revealFrame = 0;
      if (!isOpen() || (view && Math.abs(view.scale - 1) > .05)) return;
      const input = focusedText(), scroller = dialog.querySelector('.mobile-editor-panels');
      if (!input || !scroller || !scroller.contains(input)) return;
      const bounds = scroller.getBoundingClientRect(), inputBounds = input.getBoundingClientRect();
      if (!bounds.height || !inputBounds.height) return;
      const fieldBounds = (input.closest('.name-field') || input).getBoundingClientRect();
      // Include the name label when it fits. Move only this panel, never the
      // document; Safari may independently pan its visual viewport for AutoFill.
      const target = fieldBounds.height <= bounds.height - 24 ? fieldBounds : inputBounds;
      const top = bounds.top + 12, bottom = bounds.bottom - 12;
      if (target.top < top) scroller.scrollTop += target.top - top;
      else if (target.bottom > bottom) scroller.scrollTop += target.bottom - bottom;
    }
    function scheduleReveal() {
      if (!revealFrame) revealFrame = window.requestAnimationFrame(revealFocusedField);
    }
    function update() {
      if (!isOpen()) return;
      // Do not counteract pinch zoom or the browser's ordinary zoom panning.
      if (view && Math.abs(view.scale - 1) > .05) { clearSize(); lastGeometry = ''; return; }
      const height = view?.height || window.innerHeight;
      const focused = focusedText();
      if (width !== window.innerWidth) {
        // Layout and visual resize events can arrive in either order on rotation.
        // An already-open keyboard must use the new full layout height rather
        // than adopting its reduced visual height as a keyboard-free baseline.
        baseline = keyboard ? window.innerHeight : Math.min(height, window.innerHeight);
      } else if (!focused && !keyboard) baseline = Math.max(height, window.innerHeight);
      width = window.innerWidth;
      // Hysteresis avoids a full-layout flash as keyboard/rotation events settle.
      const reduced = baseline - height > (keyboard ? 64 : 120);
      // Keep space reserved through the keyboard's closing animation, including
      // the extra resize/scroll events from iOS's contact AutoFill accessory.
      keyboard = reduced && Boolean(focused || keyboard);
      dialog.style.setProperty('--mobile-editor-height', height + 'px');
      dialog.style.setProperty('--mobile-editor-top', Math.max(0, view?.offsetTop || 0) + 'px');
      dialog.dataset.keyboard = String(keyboard);
      const geometry = [height, view?.offsetTop || 0, width, keyboard].join(':');
      if (geometry !== lastGeometry || focused !== lastFocus) scheduleReveal();
      lastGeometry = geometry; lastFocus = focused;
    }
    function open() {
      baseline = Math.max(view?.height || 0, window.innerHeight);
      width = window.innerWidth;
      keyboard = false;
      previewSize(false);
      update();
    }
    function close() {
      if (revealFrame) window.cancelAnimationFrame(revealFrame);
      revealFrame = 0; keyboard = false; baseline = 0; width = 0;
      lastGeometry = ''; lastFocus = null;
      clearSize();
      if (previewButton) { previewSize(false); delete dialog.dataset.previewSize; }
    }
    function finishTyping() {
      if (!isOpen()) return;
      focusedText()?.blur();
      title?.focus({ preventScroll: true });
      update();
    }
    doneButton?.addEventListener('click', finishTyping);
    dialog.addEventListener('focusin', update);
    dialog.addEventListener('focusout', () => queueMicrotask(update));
    window.addEventListener('resize', update, { passive: true });
    view?.addEventListener('resize', update, { passive: true });
    view?.addEventListener('scroll', update, { passive: true });
    return Object.freeze({ open, close, update, finishTyping });
  }
  window.JEPersonalizationViewport = Object.freeze({ create });
})();
